import React, { useState, useEffect } from "react";
import { Input, Button, Upload } from "antd";
import {
  RiChat3Fill,
  RiAttachmentLine,
  RiCloseCircleLine,
} from "@remixicon/react";
import axios from "axios";
import "./ChatButton.css";
import chatLogo from "../assets/chat.png";

const ChatButton = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [fileList, setFileList] = useState([]);
  const [category, setCategory] = useState("");
  const [ticketId, setTicketId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const [filePreviews, setFilePreviews] = useState({});

  // Create URL previews when fileList changes
  useEffect(() => {
    // Clean up old previews to prevent memory leaks
    return () => {
      Object.values(filePreviews).forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, [filePreviews]);

  // Generate and store preview URLs for files
  const generatePreviews = (files) => {
    const newPreviews = {};
    files.forEach((file) => {
      const fileObject = file.originFileObj || file;
      if (fileObject && !filePreviews[fileObject.uid || fileObject.name]) {
        newPreviews[fileObject.uid || fileObject.name] =
          URL.createObjectURL(fileObject);
      }
    });

    setFilePreviews((prev) => ({ ...prev, ...newPreviews }));
  };

  const toggleChatWindow = () => {
    setIsChatOpen(!isChatOpen);
  };

  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    generatePreviews(newFileList);
  };

  const handleRemoveFile = (fileToRemove) => {
    const updatedFileList = fileList.filter(
      (file) =>
        (file.uid || file.name) !== (fileToRemove.uid || fileToRemove.name)
    );
    setFileList(updatedFileList);
  };

  const handleCategorySelect = (category) => {
    setCategory(category);
    let adminResponse = "";

    switch (category) {
      case "General inquiry":
        adminResponse =
          "Vous avez sélectionné Demande générale. Comment pouvons-nous vous aider ?";
        break;
      case "Technical Support":
        adminResponse =
          "Vous avez sélectionné Support technique. Veuillez décrire le problème que vous rencontrez et/ou indiquez-nous un numéro de téléphone où nous pouvons vous joindre si nécessaire.";
        break;
      case "Update Employee Data":
        adminResponse =
          "Vous avez sélectionné Mise à jour des données des employés. Veuillez fournir les détails que vous souhaitez mettre à jour.";
        break;
      default:
        adminResponse = `You selected ${category}. Please send your message.`;
        break;
    }

    setMessages([
      ...messages,
      { sender: "user", text: category },
      { sender: "admin", text: adminResponse },
    ]);
  };

  const handleSend = async () => {
    if (message.trim() === "" && fileList.length === 0) return;

    if (!category) {
      setError("Please select a category before sending a message.");
      return;
    }

    try {
      let currentTicketId = ticketId;

      if (!currentTicketId) {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/auth/tickets/create`,
          { category },
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          }
        );
        currentTicketId = response.data.data;
        setTicketId(currentTicketId);
      }

      const formData = new FormData();
      formData.append("message", message);
      formData.append("ticketId", currentTicketId);

      fileList.forEach((file) => {
        formData.append("files", file.originFileObj);
      });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/sendMessage`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      );

      // Create new message objects with text
      const newMessages = [
        ...messages,
        { sender: "user", text: response.data.data.message },
      ];

      // Add file messages with previewURLs
      fileList.forEach((file) => {
        const fileObj = file.originFileObj || file;
        const fileId = fileObj.uid || fileObj.name;

        newMessages.push({
          sender: "user",
          fileId: fileId,
          fileName: file.name,
          fileType: fileObj.type,
        });
      });

      setMessages(newMessages);
      setMessage("");
      setFileList([]);
    } catch (error) {
      console.error("Failed to send message:", error);
      setError("Failed to send the message. Please try again.");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Create preview URLs for any new files
  useEffect(() => {
    generatePreviews(fileList);
  }, [fileList]);

  return (
    <div className="chat-container">
      <button className="chat-button" onClick={toggleChatWindow}>
        <img src={chatLogo} width={80} alt="Chat" />
      </button>
      {isChatOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <h6 className="chat-header-text">Chat Support</h6>
            <button className="close-chat" onClick={toggleChatWindow}>
              X
            </button>
          </div>
          <div className="chat-body">
            {error && <div className="error-message">{error}</div>}
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`chat-bubble ${
                  msg.sender === "admin" ? "admin-bubble" : "user-bubble"
                }`}
              >
                {msg.text && <div>{msg.text}</div>}
                {/* Handle file messages */}
                {msg.fileId && filePreviews[msg.fileId] && (
                  <div>
                    {msg.fileType && msg.fileType.startsWith("image/") ? (
                      <img
                        src={filePreviews[msg.fileId]}
                        alt={msg.fileName}
                        className="file-preview"
                      />
                    ) : (
                      <a
                        href={filePreviews[msg.fileId]}
                        className="file-link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {msg.fileName}
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
            {!category && !error && (
              <div className="suggestions">
                <button
                  onClick={() => handleCategorySelect("General inquiry")}
                  className="suggestion-button"
                >
                  Demande Générale
                </button>
                <button
                  onClick={() => handleCategorySelect("Technical Support")}
                  className="suggestion-button"
                >
                  Support Technique
                </button>
                <button
                  onClick={() => handleCategorySelect("Update Employee Data")}
                  className="suggestion-button"
                >
                  Mise à jour données Employés
                </button>
              </div>
            )}
          </div>

          {/* File preview area above message input */}
          {fileList.length > 0 && (
            <div className="file-preview-area">
              <h6>Attached Files</h6>
              <div className="file-preview-list">
                {fileList.map((file, index) => {
                  const fileObj = file.originFileObj || file;
                  const fileId = fileObj.uid || fileObj.name;
                  const previewUrl = filePreviews[fileId];

                  return previewUrl ? (
                    <div key={index} className="file-preview-item">
                      {fileObj.type && fileObj.type.startsWith("image/") ? (
                        <div className="image-preview-container">
                          <img
                            src={previewUrl}
                            alt={file.name}
                            className="file-preview-image"
                          />
                          <button
                            className="remove-file-button"
                            onClick={() => handleRemoveFile(file)}
                          >
                            <RiCloseCircleLine />
                          </button>
                        </div>
                      ) : (
                        <div className="file-preview-container">
                          <a
                            href={previewUrl}
                            className="file-link"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {file.name}
                          </a>
                          <button
                            className="remove-file-button"
                            onClick={() => handleRemoveFile(file)}
                          >
                            <RiCloseCircleLine />
                          </button>
                        </div>
                      )}
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          )}

          <div className="chat-footer">
            <div className="input-group">
              <Input
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter your message here"
                onPressEnter={handleKeyPress}
                className="message-input"
              />
              <Upload
                beforeUpload={() => false}
                fileList={fileList}
                onChange={handleFileChange}
                showUploadList={false}
              >
                <Button icon={<RiAttachmentLine />} className="upload-button" />
              </Upload>
              {/* <Button
                onClick={handleSend}
                className="send-button"
                type="primary"
              >
                Send
              </Button> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatButton;
